import { useMemo, useState } from "react";
import { SparklesIcon, XMarkIcon } from "@heroicons/react/24/outline";

function AIAssistantModal({ t, filters, completions, onClose, onPostulate, onApplyFilters }) {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const questions = useMemo(
        () => filters.filter(f => f.id !== "name" && f.id !== "description" && f.options?.length > 1),
        [filters]
    );

    // Completions still matching all answers given so far
    const matchingCompletions = useMemo(
        () => completions.filter(c =>
            Object.entries(answers).every(([name, value]) => c.properties?.[name] === value)
        ),
        [completions, answers]
    );

    function handleAnswer(filterName, optionValue) {
        const newAnswers = { ...answers, [filterName]: optionValue };
        setAnswers(newAnswers);
        if (step < questions.length - 1) {
            setStep(step + 1);
        } else {
            setLoading(true);
            setTimeout(() => {
                setResult(computeBestMatch(newAnswers));
                setLoading(false);
            }, 2000);
        }
    }

    function computeBestMatch(answersObj) {
        let best = null;
        let bestScore = -1;
        const keys = Object.keys(answersObj);
        completions.forEach(c => {
            let score = 0;
            keys.forEach(name => {
                if (c.properties?.[name] === answersObj[name]) score++;
            });
            if (score > bestScore) { bestScore = score; best = c; }
        });
        const pct = keys.length > 0 ? Math.round((bestScore / keys.length) * 100) : 0;
        return { completion: best, percentage: pct };
    }

    if (questions.length === 0) return null;
    const q = questions[step];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.45)" }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 relative">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary-perso flex items-center justify-center flex-shrink-0">
                            <SparklesIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <p className="font-semibold text-sm leading-tight">{t("AI guidance assistant")}</p>
                            <p className="text-xs text-gray-400 leading-tight">
                                {t("Question progress", { current: result ? questions.length : step + 1, total: questions.length })}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 rounded-full p-1 ml-4">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {loading && (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                        <div
                            className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200"
                            style={{ borderTopColor: "var(--primary-color)" }}
                        />
                        <p className="text-gray-500 text-sm">{t("AI is analyzing your profile...")}</p>
                    </div>
                )}

                {result && !loading && (
                    <div>
                        <p className="text-xs font-bold color-primary-perso tracking-widest mb-3 flex items-center gap-1">
                            <span>✦</span> {t("Personalized recommendation")}
                        </p>
                        <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 mb-5">
                            <div className="flex justify-between items-start gap-3 mb-2">
                                <div className="min-w-0">
                                    <h3 className="font-bold text-gray-900 leading-snug" lang="fr" translate="yes">{result.completion?.question ?? result.completion?.label}</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {filters.filter(f => f.id !== "name" && f.id !== "description").slice(0, 2)
                                            .map(f => result.completion?.properties?.[f.name]).filter(Boolean).join(" · ")}
                                    </p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <span className="text-2xl font-extrabold color-primary-perso">{result.percentage}%</span>
                                    <p className="text-xs text-gray-400 leading-tight">{t("compatibility")}</p>
                                </div>
                            </div>
                            <p className="text-xs text-gray-600 leading-relaxed">
                                {t("Based on your answers, {{label}} best matches your profile.", { label: result.completion?.label })}
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => onApplyFilters(answers)}
                                className="flex-1 border border-gray-200 rounded-xl py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                {t("View all matching programs")}
                            </button>
                            <button
                                onClick={() => onPostulate(result.completion)}
                                className="flex-1 bg-primary-perso text-white rounded-xl py-3 text-sm font-semibold hover:opacity-90 transition-opacity"
                            >
                                {t("Apply now")}
                            </button>
                        </div>
                    </div>
                )}

                {!loading && !result && (
                    <div>
                        <h3 className="font-bold text-gray-900 text-lg mb-4">{q.question ?? q.label}</h3>
                        <div className="flex flex-col gap-2">
                            {q.options.filter(opt => matchingCompletions.some(c => c.properties?.[q.name] === opt.value)).map(opt => (
                                <button
                                    key={opt.value}
                                    onClick={() => handleAnswer(q.name, opt.value)}
                                    className="flex items-center justify-between w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 hover:border-primary-perso transition-colors text-left"
                                >
                                    <span>{opt.label}</span>
                                    <span className="color-primary-perso font-bold ml-2">→</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AIAssistantModal